// src/pages/NewCredentialTransfer.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, formatDate } from '../utils/api';

function CopyButton({ value }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button className="btn btn-secondary btn-sm" onClick={copy} title="Copy to clipboard" style={{ whiteSpace: 'nowrap' }}>
      {copied ? '✅ Copied' : '📋 Copy'}
    </button>
  );
}

function TagsInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const inputRef = React.useRef();
  const addTag = (val) => {
    const email = val.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (!values.includes(email)) onChange([...values, email]);
    setInput('');
  };
  return (
    <div className="tags-input-wrapper" onClick={() => inputRef.current?.focus()}>
      {values.map(v => (
        <span key={v} className="tag-chip">
          {v}
          <button className="tag-remove" onClick={e => { e.stopPropagation(); onChange(values.filter(x => x !== v)); }}>×</button>
        </span>
      ))}
      <input ref={inputRef} className="tags-input" value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addTag(input); } else if (e.key === 'Backspace' && !input) onChange(values.slice(0, -1)); }}
        onBlur={() => addTag(input)}
        placeholder={values.length === 0 ? placeholder : ''} />
    </div>
  );
}

export default function NewCredentialTransfer() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [expiryDays, setExpiryDays] = useState(2);
  const [usePassword, setUsePassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handlePasswordToggle = (checked) => setUsePassword(checked);

  const handleSubmit = async () => {
    if (!content.trim()) return setError('Please enter the credential content');
    if (!recipients.length) return setError('Please add at least one recipient');
    setError(''); setSubmitting(true);
    try {
      const data = await apiFetch('credentials', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim() || 'Shared Credentials',
          content: content.trim(),
          expiryDays,
          recipients,
          message: message.trim(),
          usePassword,
        }),
      });
      setResult(data);   // data.password is generated by the server and returned once
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  // Success screen
  if (result) {
    const expiry = new Date(result.expiresAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div>
        <div className="page-eyebrow">Credential Transfers</div>
        <h1 className="page-title">Credentials Sent</h1>
        <p className="page-subtitle">Credentials have been stored securely</p>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '56px', marginBottom: '14px' }}>✅</div>
            <h2 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>Credentials sent successfully</h2>
            <p style={{ color: 'var(--g500)', fontSize: '14px', marginBottom: '24px' }}>
              {recipients.length - (result.emailFailures || []).length} of {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} notified. Link expires <strong>{expiry}</strong>.
              Each recipient's copy is permanently deleted once they have viewed it.
            </p>
            {(result.emailFailures || []).length > 0 && (
              <div className="alert alert-error" style={{ textAlign: 'left', marginBottom: '20px' }}>
                <strong>⚠️ Notification could not be emailed to:</strong>
                {(result.emailFailures || []).map(f => <div key={f.email}>{f.email}</div>)}
                <div style={{ marginTop: '6px', fontSize: '12px' }}>The transfer was created, but these recipients have not been notified. Please contact IT Support (it.support@sufc.co.uk).</div>
              </div>
            )}
            <div style={{ background: 'var(--g50)', border: '1px solid var(--g200)', padding: '14px', marginBottom: '16px', textAlign: 'left', display: 'inline-block', minWidth: '280px' }}>
              <div style={{ fontSize: '11px', color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Notified recipients</div>
              {recipients.map(r => <div key={r} style={{ fontSize: '13px', color: 'var(--g700)', padding: '3px 0' }}>📧 {r}</div>)}
            </div>
            {result.password && (
              <div style={{ background: '#fff8e1', border: '1px solid #f59e0b', padding: '16px', marginBottom: '16px', textAlign: 'left' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>🔑 Transfer Password</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <code style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '0.14em', color: 'var(--g900)', fontFamily: 'var(--font-mono)', flex: 1 }}>{result.password}</code>
                  <CopyButton value={result.password} />
                </div>
                <div style={{ fontSize: '12px', color: 'var(--g600)' }}>⚠️ Share this password with recipients separately — not by email</div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => navigate('/credentials')}>View my transfers</button>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>Send another</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-eyebrow">Credential Transfers</div>
      <h1 className="page-title">New Credential Transfer</h1>
      <p className="page-subtitle">Share passwords, API keys and sensitive text securely — auto-expires on first view</p>

      {error && <div className="alert alert-error">⚠️ {error}</div>}

      <div className="transfer-form-grid">
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Transfer details</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Title <span style={{ color: 'var(--g400)', fontWeight: 400 }}>(optional)</span></label>
                <input className="form-input" placeholder="e.g. VPN Credentials, Database Password, API Key"
                  value={title} onChange={e => setTitle(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Recipients</label>
                <TagsInput values={recipients} onChange={setRecipients} placeholder="Type email and press Enter…" />
                <span className="form-hint">Each recipient gets a unique link and must verify via email OTP</span>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Message to recipients <span style={{ color: 'var(--g400)', fontWeight: 400 }}>(optional)</span></label>
                <textarea className="form-textarea" rows={3} maxLength={500} value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="e.g. Hi Sam — here are the documents we discussed on Tuesday." style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
                <span className="form-hint">Shown in the email, so recipients know it's genuinely from you. Please don't include links or passwords. {message.length}/500</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Credential content</span></div>
            <div className="card-body">
              <textarea className="form-textarea" rows={10}
                placeholder="Paste or type passwords, API keys, tokens, connection strings or any sensitive text here…"
                value={content} onChange={e => setContent(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
              <span className="form-hint" style={{ marginTop: '8px', display: 'block' }}>
                This content is AES-256 encrypted at rest and permanently deleted after the first recipient views it.
              </span>
              {content && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--g400)' }}>
                  {content.length} characters
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="card" style={{ borderLeft: '3px solid var(--red)' }}>
            <div className="card-body" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--red)', marginBottom: '10px' }}>🔒 Security</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  ['🔐', 'Content is AES-256 encrypted at rest'],
                  ['📧', 'Recipients verify identity via one-time email code before access'],
                  ['👁', 'Content is permanently deleted after the first view'],
                  ['⏱', 'Links auto-expire — you control when'],
                  ['📋', 'Every access attempt is logged in a full audit trail'],
                  ['🔑', 'Optional password adds a second layer of protection'],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
                    <span style={{ fontSize: '12px', color: 'var(--g600)', lineHeight: '1.4' }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Expiry</span></div>
            <div className="card-body">
              <p style={{ fontSize: '12px', color: 'var(--g500)', marginBottom: '10px' }}>How long should this link be active?</p>
              <div className="expiry-options">
                {[1,2,3,4,5].map(d => (
                  <button key={d} className={`expiry-option${expiryDays === d ? ' selected' : ''}`} onClick={() => setExpiryDays(d)}>
                    <span className="days">{d}</span>
                    <span className="label">{d === 1 ? 'day' : 'days'}</span>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '10px' }}>Content is permanently deleted on first view regardless of date</p>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Optional Features</span></div>
            <div className="card-body">
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={usePassword} onChange={e => handlePasswordToggle(e.target.checked)}
                  style={{ marginTop: '3px', accentColor: 'var(--red)', width: '15px', height: '15px' }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--g800)' }}>🔑 Password protection</div>
                  <div style={{ fontSize: '12px', color: 'var(--g500)', marginTop: '2px' }}>Recipients must enter a password in addition to the email OTP</div>
                </div>
              </label>
              {usePassword && (
                <div style={{ marginTop: '10px', padding: '12px', background: 'var(--g50)', border: '1px solid var(--g200)' }}>
                  <div style={{ fontSize: '12px', color: 'var(--g700)', lineHeight: 1.5 }}>
                    A secure password will be <strong>generated for you when you send</strong>. It's shown once on the confirmation screen — share it with the recipient separately, not by email.
                  </div>
                </div>
              )}
            </div>
          </div>

          <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
            onClick={handleSubmit} disabled={submitting || !content.trim() || !recipients.length}>
            {submitting ? 'Sending…' : '🔐 Send Credentials'}
          </button>
        </div>
      </div>
    </div>
  );
}
